import React, { useState, useEffect } from 'react';
import {
	Users,
	DollarSign,
	Clock,
	CheckCircle,
	XCircle,
	AlertCircle,
	RefreshCw,
	MessageSquare,
	Calendar,
	User
} from 'lucide-react';
import { offersService, showToast } from '../services';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const OffersSection = ({ listingId, listing }) => {
	const { user } = useAuth();
	const [offers, setOffers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [updatingOffer, setUpdatingOffer] = useState(null);

	// Check if the current user is the seller of this listing
	const isSeller = () => {
		if (!listing || !user) return false;
		// Compare seller email with user email
		const sellerEmail = listing?.seller?.email || '';
		const userEmail = user?.email || '';
		return sellerEmail.toLowerCase() === userEmail.toLowerCase();
	};

	useEffect(() => {
		if (listingId) {
			fetchOffers();
		}
	}, [listingId]);

	const fetchOffers = async () => {
		try {
			setLoading(true);
			const response = await offersService.getByListingId(listingId);
			if (response.success) {
				setOffers(response.data || []);
			} else {
				toast.error('Failed to fetch offers');
				setOffers([]);
			}
		} catch (error) {
			console.error('Error fetching offers:', error);
			toast.error('Error fetching offers');
			setOffers([]);
		} finally {
			setLoading(false);
		}
	};

	const handleOfferStatusUpdate = async (offerId, newStatus) => {
		try {
			setUpdatingOffer(offerId);
			let response;
			
			// Use the correct API endpoint based on action
			if (newStatus === 'accepted') {
				response = await offersService.acceptOffer(offerId);
			} else if (newStatus === 'rejected') {
				response = await offersService.rejectOffer(offerId);
			} else {
				response = await offersService.updateStatus(offerId, { status: newStatus });
			}
			
			if (response.success) {
				toast.success(`Offer ${newStatus} successfully`);
				// Update the local state
				setOffers(prevOffers => 
					prevOffers.map(offer => 
						offer.id === offerId 
							? { ...offer, status: newStatus }
							: offer
					)
				);
			} else {
				toast.error(response.message || `Failed to ${newStatus} offer`);
			}
		} catch (error) {
			console.error('Error updating offer status:', error);
			toast.error(`Error updating offer status`);
		} finally {
			setUpdatingOffer(null);
		}
	};

	const getStatusIcon = (status) => {
		switch (status) {
			case 'pending':
				return <Clock size={16} className="text-yellow-500" />;
			case 'accepted':
				return <CheckCircle size={16} className="text-green-500" />;
			case 'rejected':
				return <XCircle size={16} className="text-red-500" />;
			default:
				return <AlertCircle size={16} className="text-gray-500" />;
		}
	};

	const getStatusColor = (status) => {
		switch (status) {
			case 'pending':
				return { backgroundColor: '#fef3c7', color: '#92400e' };
			case 'accepted':
				return { backgroundColor: '#dcfce7', color: '#166534' };
			case 'rejected':
				return { backgroundColor: '#fee2e2', color: '#991b1b' };
			default:
				return { backgroundColor: '#f3f4f6', color: '#374151' };
		}
	};

	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD'
		}).format(amount);
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	if (loading) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 p-6">
				<div className="flex items-center justify-center py-8">
					<RefreshCw size={24} className="animate-spin text-blue-500" />
					<span className="ml-2 text-gray-600">Loading offers...</span>
				</div>
			</div>
		);
	}

	return (
		<div style={{
			backgroundColor: 'white',
			borderRadius: '12px',
			border: '1px solid #e5e7eb',
			boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
			overflow: 'hidden'
		}}>
			{/* Header */}
			<div style={{
				background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
				padding: '20px 24px',
				color: 'white'
			}}>
				<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						<div style={{
							width: '40px',
							height: '40px',
							borderRadius: '50%',
							backgroundColor: 'rgba(255, 255, 255, 0.2)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}>
							<Users size={20} />
						</div>
						<div>
							<h3 style={{ fontSize: '20px', fontWeight: '700', margin: '0 0 4px 0' }}>
								Offers
							</h3>
							<p style={{ fontSize: '14px', opacity: 0.9, margin: 0 }}>
								Manage buyer offers for this listing
							</p>
						</div>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
						<div style={{
							backgroundColor: 'rgba(255, 255, 255, 0.2)',
							padding: '8px 16px',
							borderRadius: '20px',
							fontSize: '14px',
							fontWeight: '600'
						}}>
							{offers.length} {offers.length === 1 ? 'Offer' : 'Offers'}
						</div>
						<button
							onClick={fetchOffers}
							disabled={loading}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								padding: '8px 16px',
								backgroundColor: 'rgba(255, 255, 255, 0.2)',
								color: 'white',
								border: 'none',
								borderRadius: '8px',
								fontSize: '14px',
								fontWeight: '500',
								cursor: loading ? 'not-allowed' : 'pointer',
								opacity: loading ? 0.7 : 1,
								transition: 'all 0.2s ease'
							}}
							onMouseOver={(e) => !loading && (e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.3)')}
							onMouseOut={(e) => !loading && (e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)')}
						>
							<RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
							Refresh
						</button>
					</div>
				</div>
			</div>

			{/* Content */}
			<div style={{ padding: '24px' }}>
				{offers.length === 0 ? (
					<div style={{
						textAlign: 'center',
						padding: '60px 20px',
						backgroundColor: '#f8fafc',
						borderRadius: '12px',
						border: '2px dashed #cbd5e1'
					}}>
						<div style={{
							width: '80px',
							height: '80px',
							margin: '0 auto 24px',
							backgroundColor: '#e2e8f0',
							borderRadius: '50%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center'
						}}>
							<Users size={32} style={{ color: '#94a3b8' }} />
						</div>
						<h4 style={{
							fontSize: '18px',
							fontWeight: '600',
							color: '#475569',
							margin: '0 0 8px 0'
						}}>
							No Offers Yet
						</h4>
						<p style={{
							fontSize: '14px',
							color: '#64748b',
							margin: '0 0 16px 0',
							maxWidth: '300px',
							margin: '0 auto 16px'
						}}>
							This listing hasn't received any offers yet. When buyers make offers, they'll appear here.
						</p>
						<div style={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: '8px',
							padding: '8px 16px',
							backgroundColor: '#f1f5f9',
							borderRadius: '8px',
							fontSize: '12px',
							color: '#64748b'
						}}>
							<Clock size={14} />
							Check back later for updates
						</div>
					</div>
				) : (
				<div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
					{offers.map((offer) => (
						<div
							key={offer.id}
							style={{
								border: '1px solid #e5e7eb',
								borderRadius: '12px',
								padding: '20px',
								backgroundColor: 'white',
								boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
								transition: 'all 0.2s ease',
								position: 'relative',
								overflow: 'hidden'
							}}
							onMouseEnter={(e) => {
								e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
								e.target.style.transform = 'translateY(-2px)';
							}}
							onMouseLeave={(e) => {
								e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
								e.target.style.transform = 'translateY(0)';
							}}
						>
							{/* Status indicator bar */}
							<div style={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								height: '4px',
								backgroundColor: offer.status === 'pending' ? '#f59e0b' : 
												offer.status === 'accepted' ? '#10b981' : '#ef4444'
							}}></div>

							<div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px' }}>
								<div style={{ flex: 1 }}>
									{/* Header with user and status */}
									<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
										<div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
											<div style={{
												width: '40px',
												height: '40px',
												borderRadius: '50%',
												backgroundColor: '#f3f4f6',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center'
											}}>
												<User size={20} style={{ color: '#6b7280' }} />
											</div>
											<div>
												<h4 style={{
													fontSize: '16px',
													fontWeight: '600',
													color: '#111827',
													margin: '0 0 4px 0'
												}}>
													{offer.user_name || 'Anonymous User'}
												</h4>
												<p style={{
													fontSize: '12px',
													color: '#6b7280',
													margin: 0
												}}>
													Offer #{offer.id}
												</p>
											</div>
										</div>
										<div style={{
											display: 'flex',
											alignItems: 'center',
											gap: '6px',
											padding: '6px 12px',
											borderRadius: '20px',
											fontSize: '12px',
											fontWeight: '600',
											...getStatusColor(offer.status)
										}}>
											{getStatusIcon(offer.status)}
											{offer.status.charAt(0).toUpperCase() + offer.status.slice(1)}
										</div>
									</div>

									{/* Offer amount */}
									<div style={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										marginBottom: '16px',
										padding: '16px',
										backgroundColor: '#f0fdf4',
										borderRadius: '8px',
										border: '1px solid #bbf7d0'
									}}>
										<div style={{
											width: '32px',
											height: '32px',
											borderRadius: '50%',
											backgroundColor: '#22c55e',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center'
										}}>
											<DollarSign size={16} style={{ color: 'white' }} />
										</div>
										<div>
											<p style={{
												fontSize: '12px',
												color: '#166534',
												margin: '0 0 4px 0',
												fontWeight: '500'
											}}>
												Offer Amount
											</p>
											<p style={{
												fontSize: '24px',
												fontWeight: '700',
												color: '#15803d',
												margin: 0
											}}>
												{formatCurrency(offer.amount)}
											</p>
										</div>
									</div>

									{/* Message */}
									{offer.message && (
										<div style={{
											display: 'flex',
											gap: '12px',
											marginBottom: '16px',
											padding: '16px',
											backgroundColor: '#f8fafc',
											borderRadius: '8px',
											border: '1px solid #e2e8f0'
										}}>
											<MessageSquare size={20} style={{ color: '#6b7280', marginTop: '2px' }} />
											<div>
												<p style={{
													fontSize: '12px',
													color: '#64748b',
													margin: '0 0 4px 0',
													fontWeight: '500'
												}}>
													Message from buyer
												</p>
												<p style={{
													fontSize: '14px',
													color: '#374151',
													margin: 0,
													lineHeight: '1.5'
												}}>
													"{offer.message}"
												</p>
											</div>
										</div>
									)}

									{/* Date and expiration info */}
									<div style={{ display: 'flex', gap: '24px', fontSize: '12px', color: '#6b7280' }}>
										<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
											<Calendar size={14} />
											<span>Submitted: {formatDate(offer.created_at)}</span>
										</div>
										{offer.expires_at && (
											<div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
												<Clock size={14} />
												<span>Expires: {formatDate(offer.expires_at)}</span>
											</div>
										)}
									</div>
								</div>

								{/* Action buttons - Only for sellers */}
								{offer.status === 'pending' && (
									<div style={{ minWidth: '120px' }}>
										{!isSeller() ? (
											<div style={{
												padding: '16px',
												backgroundColor: '#f8fafc',
												borderRadius: '8px',
												border: '1px solid #e2e8f0',
												textAlign: 'center'
											}}>
												<p style={{
													fontSize: '12px',
													color: '#64748b',
													margin: '0 0 4px 0',
													fontWeight: '500'
												}}>
													{user?.role === 'Super Admin' || user?.role === 'Store Manager' || user?.role === 'Authenticator' ? 'Admin View' : 'View Only'}
												</p>
												<p style={{
													fontSize: '11px',
													color: '#94a3b8',
													margin: 0
												}}>
													Only the listing seller can accept/reject offers
												</p>
											</div>
										) : (
											<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
												<button
													onClick={() => handleOfferStatusUpdate(offer.id, 'accepted')}
													disabled={updatingOffer === offer.id}
													style={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														gap: '8px',
														padding: '12px 16px',
														backgroundColor: '#22c55e',
														color: 'white',
														border: 'none',
														borderRadius: '8px',
														fontSize: '14px',
														fontWeight: '600',
														cursor: updatingOffer === offer.id ? 'not-allowed' : 'pointer',
														opacity: updatingOffer === offer.id ? 0.7 : 1,
														transition: 'all 0.2s ease',
														boxShadow: '0 2px 4px rgba(34, 197, 94, 0.3)'
													}}
													onMouseEnter={(e) => {
														if (updatingOffer !== offer.id) {
															e.target.style.backgroundColor = '#16a34a';
															e.target.style.transform = 'translateY(-1px)';
															e.target.style.boxShadow = '0 4px 8px rgba(34, 197, 94, 0.4)';
														}
													}}
													onMouseLeave={(e) => {
														if (updatingOffer !== offer.id) {
															e.target.style.backgroundColor = '#22c55e';
															e.target.style.transform = 'translateY(0)';
															e.target.style.boxShadow = '0 2px 4px rgba(34, 197, 94, 0.3)';
														}
													}}
												>
													{updatingOffer === offer.id ? (
														<RefreshCw size={16} className="animate-spin" />
													) : (
														<CheckCircle size={16} />
													)}
													Accept
												</button>
												<button
													onClick={() => handleOfferStatusUpdate(offer.id, 'rejected')}
													disabled={updatingOffer === offer.id}
													style={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														gap: '8px',
														padding: '12px 16px',
														backgroundColor: '#ef4444',
														color: 'white',
														border: 'none',
														borderRadius: '8px',
														fontSize: '14px',
														fontWeight: '600',
														cursor: updatingOffer === offer.id ? 'not-allowed' : 'pointer',
														opacity: updatingOffer === offer.id ? 0.7 : 1,
														transition: 'all 0.2s ease',
														boxShadow: '0 2px 4px rgba(239, 68, 68, 0.3)'
													}}
													onMouseEnter={(e) => {
														if (updatingOffer !== offer.id) {
															e.target.style.backgroundColor = '#dc2626';
															e.target.style.transform = 'translateY(-1px)';
															e.target.style.boxShadow = '0 4px 8px rgba(239, 68, 68, 0.4)';
														}
													}}
													onMouseLeave={(e) => {
														if (updatingOffer !== offer.id) {
															e.target.style.backgroundColor = '#ef4444';
															e.target.style.transform = 'translateY(0)';
															e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.3)';
														}
													}}
												>
													{updatingOffer === offer.id ? (
														<RefreshCw size={16} className="animate-spin" />
													) : (
														<XCircle size={16} />
													)}
													Reject
												</button>
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					))}
				</div>
			)}
			</div>
		</div>
	);
};

export default OffersSection;
